// Copied verbatim from eval/dashboard/internal_web/src/components/session/useSlopeTween.ts (ATM-57). Keep in step until ATM-60 shares it.
/**
 * Glides the slope chart's lines from one answer's layout to the next, so a reader stepping
 * through the consult sees each disease move rather than the chart being redrawn. Keyed on the
 * disease, so a line keeps its identity across steps — one leaving the top slides down into
 * "below" instead of vanishing. Rows move with the same timing through a CSS transition.
 */

import { useEffect, useRef, useState } from "react";

import { prefersReducedMotion } from "../../lib/motion";

/** A line's two ends, in slots (fractional while it moves). */
export type LinePosition = { from: number; to: number };

/**
 * The step animation, shared by the curves here and the rows' transition: long enough to follow,
 * short enough never to wait on.
 */
export const STEP_MS = 300;

/** Ease-out cubic — the curve CSS `ease-out` approximates, so lines and rows arrive together. */
export function easeOut(progress: number): number {
  return 1 - (1 - progress) ** 3;
}

/**
 * Where every target line is at `progress` (0 … 1) of the way from `start`. A line new to the
 * chart has no start, so it appears in place (the chart fades it in) rather than flying in.
 */
export function interpolate(
  start: ReadonlyMap<string, LinePosition>,
  target: ReadonlyMap<string, LinePosition>,
  progress: number,
): Map<string, LinePosition> {
  const eased = easeOut(Math.min(1, Math.max(0, progress)));
  const out = new Map<string, LinePosition>();
  for (const [key, end] of target) {
    const begin = start.get(key) ?? end;
    out.set(key, {
      from: begin.from + (end.from - begin.from) * eased,
      to: begin.to + (end.to - begin.to) * eased,
    });
  }
  return out;
}

/** `target`, reached over `STEP_MS` from wherever the lines were drawn last. */
export function useTweenedLines(
  target: ReadonlyMap<string, LinePosition>,
): ReadonlyMap<string, LinePosition> {
  const [drawn, setDrawn] = useState(target);
  const drawnRef = useRef(drawn);
  const targetRef = useRef(target);
  targetRef.current = target;
  // A string, so the animation restarts when the layout changes and not on every render.
  const key = [...target].map(([name, at]) => `${name}\u0000${at.from}\u0000${at.to}`).join("\u0001");

  useEffect(() => {
    const end = targetRef.current;
    const start = drawnRef.current;
    if (prefersReducedMotion()) {
      drawnRef.current = end;
      setDrawn(end);
      return;
    }
    const began = performance.now();
    let frame = requestAnimationFrame(function step(now) {
      const progress = (now - began) / STEP_MS;
      const next = interpolate(start, end, progress);
      drawnRef.current = next;
      setDrawn(next);
      if (progress < 1) frame = requestAnimationFrame(step);
    });
    return () => cancelAnimationFrame(frame);
  }, [key]);

  return drawn;
}
